import { ANALYTIKA_ACTIONS, ANALYTIKA_SCREENS } from './enums';
import { Delivery } from '../../api/models/deliveries';
import { LatLng, Location, SearchLocation } from '../../api/models/locations';

type EventTextNumField = number | string | undefined;

const authTokenRegex = /Bearer\s[\w-]+\.[\w-]+\.[\w-]+/g;
const authTokenReplacement = '*****AUTH_TOKEN_REPLACED*****';

export const replaceAuthInfo = (logText: string) => logText.replace(authTokenRegex, authTokenReplacement);

export const topUpClicked = (
  value: { wallet_balance: string; default_payment_method_flag: boolean },
  screen?: ANALYTIKA_SCREENS
) => ({
  eventName: 'top_up_clicked',
  value: JSON.stringify(value),
  screen: screen || ANALYTIKA_SCREENS.PROFILE
});

export const profileScreenOpen = (value?: {
  use_credit: boolean;
  primary_payment_method: number;
  secondary_payment_method: number;
  saved_address_count: number;
  payment_methods_count: number;
  wallet_balance: number;
  currency: string;
}) => ({
  eventName: 'profile_screen_open',
  value: JSON.stringify(value),
  screen: ANALYTIKA_SCREENS.PROFILE
});

export const addressesScreenOpen = (value: { saved_address_count: number; saved_addresses: Location[] }) => ({
  eventName: 'addresses_screen_open',
  value: JSON.stringify(value),
  screen: ANALYTIKA_SCREENS.ADDRESSES
});

export const createNewAddressClicked = () => ({
  eventName: 'add_new_address_clicked',
  screen: ANALYTIKA_SCREENS.ADDRESSES
});

export const removeAddressClicked = (value: { address_removed: Location }) => ({
  eventName: 'remove_clicked',
  value: JSON.stringify(value),
  screen: ANALYTIKA_SCREENS.ADDRESSES
});

export const setDefaultAddressClicked = (value: { address_marked_as_default: Location }) => ({
  eventName: 'default_clicked',
  value: JSON.stringify(value),
  screen: ANALYTIKA_SCREENS.ADDRESSES
});

export const paymentsScreenOpen = (value: {
  primary_payment_method: number;
  secondary_payment_method: number;
  payment_methods_count: number;
  wallet_balance: number;
  currency: string;
}) => ({
  eventName: 'payments_screen_open',
  value: JSON.stringify(value),
  screen: ANALYTIKA_SCREENS.PAYMENTS
});

export const savePaySettingsClicked = (value: {
  primary_payment_method: number;
  secondary_payment_method: number;
}) => ({
  eventName: 'save_settings_clicked',
  value: JSON.stringify(value),
  screen: ANALYTIKA_SCREENS.PAYMENTS
});

export const paymentMethodDropDownMenuClicked = (value: { paymentType: string; id?: number; cardType?: string }) => ({
  eventName: 'payment_method_drop_down_menu_clicked',
  value: JSON.stringify(value),
  screen: ANALYTIKA_SCREENS.PROFILE
});

export const paymentMethodDropDownMenuOpen = (value: { paymentType: string; id: number; cardType?: string }) => ({
  eventName: 'payment_method_drop_down_menu_open',
  value: JSON.stringify(value),
  screen: ANALYTIKA_SCREENS.PROFILE
});

export const setDefaultPaymentClicked = (value: { paymentType: string; id?: number; cardType?: string }) => ({
  eventName: 'set_default_payment_clicked',
  value: JSON.stringify(value),
  screen: ANALYTIKA_SCREENS.PROFILE
});

export const setSecondaryPaymentClicked = (value: { paymentType: string; id: number; cardType?: string }) => ({
  eventName: 'set_secondary_payment_clicked',
  value: JSON.stringify(value),
  screen: ANALYTIKA_SCREENS.PROFILE
});

export const removePaymentClicked = (value: { paymentType: string; id: number; cardType?: string }) => ({
  eventName: 'remove_payment_clicked',
  value: JSON.stringify(value),
  screen: ANALYTIKA_SCREENS.PROFILE
});

export const addAmountScreenOpen = () => ({
  eventName: 'add_amount_screen_open',
  screen: ANALYTIKA_SCREENS.AMOUNT
});

export const addAmountScreenNextClicked = (value: { amount_text: number; transaction_id: string }) => ({
  eventName: 'next_clicked',
  value: JSON.stringify(value),
  screen: ANALYTIKA_SCREENS.AMOUNT
});

export const addAmountScreenCancelClicked = (value: { amount_text: number }) => ({
  eventName: 'cancel_clicked',
  value: JSON.stringify(value),
  screen: ANALYTIKA_SCREENS.AMOUNT
});

export const creditAddedScreenOpen = (value: { amount: number | null; currency: string | null }) => ({
  eventName: 'credit_added_screen_open',
  value: JSON.stringify(value),
  screen: ANALYTIKA_SCREENS.CREDIT_ADDED
});

export const backToCeClicked = (value: { amount: number | null; currency: string | null }) => ({
  eventName: 'back_to_ce_clicked',
  value: JSON.stringify(value),
  screen: ANALYTIKA_SCREENS.CREDIT_ADDED
});

export const insufficientWalletBalancePageOpen = (value: { wallet_balance: number; currency: string }) => ({
  eventName: 'insufficient_wallet_balance_page_open',
  value: JSON.stringify(value),
  screen: ANALYTIKA_SCREENS.INSUFFICIENT_WALLET_BALANCE_ERROR
});

export const insufficientWalletBalanceVisitFaqClicked = (value: { wallet_balance: number; currency: string }) => ({
  eventName: 'visit_faq_clicked',
  value: JSON.stringify(value),
  screen: ANALYTIKA_SCREENS.INSUFFICIENT_WALLET_BALANCE_ERROR
});

export const insufficientWalletBalanceGoToSavedPaymentMethods = (value: {
  wallet_balance: number;
  currency: string;
}) => ({
  eventName: 'go_to_saved_payment_methods',
  value: JSON.stringify(value),
  screen: ANALYTIKA_SCREENS.INSUFFICIENT_WALLET_BALANCE_ERROR
});

export const insufficientWalletBalanceCancelClicked = (value: { wallet_balance: number; currency: string }) => ({
  eventName: 'cancel_clicked',
  value: JSON.stringify(value),
  screen: ANALYTIKA_SCREENS.INSUFFICIENT_WALLET_BALANCE_ERROR
});

export const noSecondaryMethodErrorPageOpen = (value: { wallet_balance: number; currency: string }) => ({
  eventName: 'no_secondary_method_selected_page_open',
  value: JSON.stringify(value),
  screen: ANALYTIKA_SCREENS.NO_SECONDARY_METHOD_SELECTED_ERROR
});

export const noSecondaryMethodErrorGoToSavedPaymentMethods = (value: { wallet_balance: number; currency: string }) => ({
  eventName: 'go_to_saved_payment_methods_clicked',
  value: JSON.stringify(value),
  screen: ANALYTIKA_SCREENS.NO_SECONDARY_METHOD_SELECTED_ERROR
});

export const noSecondaryMethodErrorCancelClicked = (value: { wallet_balance: number; currency: string }) => ({
  eventName: 'cancel_clicked',
  value: JSON.stringify(value),
  screen: ANALYTIKA_SCREENS.NO_SECONDARY_METHOD_SELECTED_ERROR
});

export const technicalErrorPageOpen = (value: { invoiceId: string }) => ({
  eventName: 'technical_error_page_open',
  value: JSON.stringify(value),
  screen: ANALYTIKA_SCREENS.TECHNICAL_ERROR
});

export const technicalErrorCancelClicked = (value: { wallet_balance: number | null; currency: string | null }) => ({
  eventName: 'cancel_clicked',
  value: JSON.stringify(value),
  screen: ANALYTIKA_SCREENS.TECHNICAL_ERROR
});

export const requestDeliveryScreenOpen = (
  value: { active_deliveries_count: number | null },
  action?: ANALYTIKA_ACTIONS
) => ({
  eventName: 'request_delivery_screen_open',
  value: JSON.stringify(value),
  screen: ANALYTIKA_SCREENS.MARKETPLACE_REQUEST_DELIVERY,
  action
});

export const requestDeliveryClicked = (value: any) => ({
  eventName: 'request_delivery_clicked',
  value: JSON.stringify(value),
  screen: ANALYTIKA_SCREENS.MARKETPLACE_REQUEST_DELIVERY
});

export const requestDeliveryFormValidationFailed = (value: any) => ({
  eventName: 'request_delivery_form_validation_failed',
  value: JSON.stringify(value),
  screen: ANALYTIKA_SCREENS.MARKETPLACE_REQUEST_DELIVERY
});

export const requestDeliveryPaymentMethodFailed = (value: any) => ({
  eventName: 'request_delivery_payment_method_failed',
  value: JSON.stringify(value),
  screen: ANALYTIKA_SCREENS.MARKETPLACE_REQUEST_DELIVERY
});

export const requestDeliverySubmitFailed = (value: any) => ({
  eventName: 'request_delivery_submit_failed',
  value: JSON.stringify(value),
  screen: ANALYTIKA_SCREENS.MARKETPLACE_REQUEST_DELIVERY
});

export const searchIconClicked = (screen: ANALYTIKA_SCREENS, value: any) => ({
  eventName: 'search_icon_clicked',
  value: JSON.stringify(value),
  screen: screen
});

export const callCareClicked = (screen: ANALYTIKA_SCREENS) => ({
  eventName: 'call_care_clicked',
  value: undefined,
  screen: screen
});

export const myProfileClicked = (screen: ANALYTIKA_SCREENS) => ({
  eventName: 'my_profile_clicked',
  value: undefined,
  screen: screen
});

export const logOutClicked = (screen: ANALYTIKA_SCREENS) => ({
  eventName: 'log_out_clicked',
  value: undefined,
  screen: screen
});

export const dedicatedFleetClicked = (screen: ANALYTIKA_SCREENS) => ({
  eventName: 'dedicated_fleet_clicked',
  screen: screen
});

export const faqClicked = (screen: ANALYTIKA_SCREENS) => ({
  eventName: 'faq_clicked',
  screen: screen
});

export const requestForDeliveryTopClicked = (screen: ANALYTIKA_SCREENS) => ({
  eventName: 'request_for_delivery_top_clicked',
  screen: screen
});

export const activeDeliveriesTopClicked = (screen: ANALYTIKA_SCREENS) => ({
  eventName: 'active_deliveries_top_menu_clicked',
  screen: screen
});

export const completedDeliveriesTopMenuClicked = (screen: ANALYTIKA_SCREENS) => ({
  eventName: 'completed_deliveries_top_menu_clicked',
  screen: screen
});

export const dashboardTopMenuClicked = (screen: ANALYTIKA_SCREENS) => ({
  eventName: 'dashboard_top_menu_clicked',
  screen: screen
});

export const generateTokenTopMenuClicked = (screen: ANALYTIKA_SCREENS) => ({
  eventName: 'generate_token_top_menu_clicked',
  screen: screen
});

export const activeDeliveriesOtpTopMenuClicked = (screen: ANALYTIKA_SCREENS) => ({
  eventName: 'active_deliveries_otp_top_menu_clicked',
  screen: screen
});

export const marketplaceClicked = (screen: ANALYTIKA_SCREENS) => ({
  eventName: 'marketplace_side_menu_clicked',
  screen: screen
});

export const activeDeliveriesSideClicked = (value: {
  active_deliveries_count: number;
  active_deliveries_list: Delivery[];
}) => ({
  eventName: 'active_deliveries_side_clicked',
  value: JSON.stringify(value),
  screen: ANALYTIKA_SCREENS.MARKETPLACE_REQUEST_DELIVERY
});

export const deliveryDetailsClicked = (
  screen: ANALYTIKA_SCREENS,
  value: { delivery_details: Delivery | undefined }
) => ({
  eventName: 'delivery_details_clicked',
  value: JSON.stringify(value),
  screen: screen
});

export const deliveriesPaginationClicked = (
  screen: ANALYTIKA_SCREENS,
  value: { current_page: number; deliveries_count: number }
) => ({
  eventName: 'deliveries_pagination_clicked',
  value: JSON.stringify(value),
  screen: screen
});

export const cancelThisDeliveryClicked = (screen: ANALYTIKA_SCREENS, value: Delivery) => ({
  eventName: 'cancel_this_delivery_clicked',
  value: JSON.stringify(value),
  screen: screen
});

export const noDontCancelClicked = (screen: ANALYTIKA_SCREENS, value: Delivery) => ({
  eventName: 'no_dont_cancel_clicked',
  value: JSON.stringify(value),
  screen: screen
});

export const yesCancelClicked = (screen: ANALYTIKA_SCREENS, value: Delivery, cancellation_reason: string) => ({
  eventName: 'yes_cancel_clicked',
  value: JSON.stringify(value),
  cancellation_reason,
  screen: screen
});

export const completedDeliveriesSideClicked = (value: { completed_deliveries_list: Delivery[] }) => ({
  eventName: 'completed_deliveries_side_clicked',
  value: JSON.stringify(value),
  screen: ANALYTIKA_SCREENS.MARKETPLACE_REQUEST_DELIVERY
});

export const cnfCreateAgainClicked = (
  screen: ANALYTIKA_SCREENS,
  value?: Delivery | { id: number | string; bookingId: string }
) => ({
  eventName: 'cnf_create_again_clicked',
  value: JSON.stringify(value),
  screen: screen
});

export const activeDeliveriesPageOpen = (value: { completed_deliveries_list: Delivery[] }) => ({
  eventName: 'active_deliveries_page_open',
  value: JSON.stringify(value),
  screen: ANALYTIKA_SCREENS.MARKETPLACE_ACTIVE_DELIVERIES
});

export const completedDeliveriesPageOpen = (value: { completed_deliveries_list: Delivery[] }) => ({
  eventName: 'completed_deliveries_page_open',
  value: JSON.stringify(value),
  screen: ANALYTIKA_SCREENS.MARKETPLACE_ACTIVE_DELIVERIES
});

export const downloadRecordsClicked = () => ({
  eventName: 'download_records_clicked',
  screen: ANALYTIKA_SCREENS.MARKETPLACE_COMPLETED_DELIVERIES
});

export const raiseEscalationClicked = (value: { delivery_id: number | string }) => ({
  eventName: 'raise_escalation_clicked',
  value: JSON.stringify(value),
  screen: ANALYTIKA_SCREENS.MARKETPLACE_COMPLETED_DELIVERIES
});

export const dashboardPageOpen = (value: {
  date_from: Date | undefined;
  date_to: Date | undefined;
  completed_deliveries_count: EventTextNumField;
  cancelled_deliveries_count: EventTextNumField;
  average_deliveries_per_day_count: EventTextNumField;
  cancelled_deliveries_percentage: EventTextNumField;
  cnf_cancelled_deliveries_count: EventTextNumField;
  cancelled_by_merchant_count: EventTextNumField;
  cancelled_by_captain_count: EventTextNumField;
  cancelled_by_care_count: EventTextNumField;
  time_to_complete_delivery: EventTextNumField;
  time_to_assign_captain: EventTextNumField;
  time_to_reach_merchant: EventTextNumField;
  captain_waiting_time_at_merchant: EventTextNumField;
  time_between_pickup_dropoff: EventTextNumField;
  distance_between_assignment_pickup: EventTextNumField;
  distance_between_pickup_dropoff: EventTextNumField;
  delivery_time_when_dropoff_available: EventTextNumField;
  delivery_time_when_dropoff_not_available: EventTextNumField;
  delivery_distance_when_dropoff_available: EventTextNumField;
  delivery_distance_when_dropoff_not_available: EventTextNumField;
}) => ({
  eventName: 'dashboard_page_open',
  value: JSON.stringify(value),
  screen: ANALYTIKA_SCREENS.MARKETPLACE_DASHBOARD
});

export const ringfencingError = (value: {
  calculated_distance: EventTextNumField;
  pickup_location_id?: EventTextNumField;
  pickup_location_name?: EventTextNumField;
  dropoff_location_id?: EventTextNumField;
  dropoff_location_name: EventTextNumField;
  pickup_lat_lng: LatLng | undefined;
  dropoff_lat_lng: LatLng | undefined;
}) => ({
  eventName: 'ringfencing_error',
  value: JSON.stringify(value),
  screen: ANALYTIKA_SCREENS.MARKETPLACE_REQUEST_DELIVERY
});

export const genericNetworkErrorSample = (errorInfo: any, apiName?: string) => ({
  eventName: 'http_request_failed',
  value: replaceAuthInfo(JSON.stringify(errorInfo)),
  screen: 'background_network_error',
  api_name: apiName
});

export const merchantConfigFetchingCfdScheduled = (value: { time: number }) => ({
  eventName: 'merchant_profile_fetching_cfd_scheduled',
  value: JSON.stringify(value),
  screen: 'background_network_error'
});

export const zeroWalletBalanceAlertDisplayed = () => ({
  eventName: 'zero_wallet_balance_alert_displayed',
  screen: ANALYTIKA_SCREENS.MARKETPLACE_REQUEST_DELIVERY
});

export const zeroWalletBalanceAlertTopUpClicked = () => ({
  eventName: 'zero_wallet_balance_alert_top_up_clicked',
  screen: ANALYTIKA_SCREENS.MARKETPLACE_REQUEST_DELIVERY
});

export const bookingChargeToCardAlertDisplayed = () => ({
  eventName: 'booking_charge_to_card_alert_displayed',
  screen: ANALYTIKA_SCREENS.MARKETPLACE_REQUEST_DELIVERY
});

export const bookingChargeToCardAlertTopUpClicked = () => ({
  eventName: 'booking_charge_to_card_alert_top_up_clicked',
  screen: ANALYTIKA_SCREENS.MARKETPLACE_REQUEST_DELIVERY
});

export const bookingChargeToCardAlertChangePaymentMethodClicked = () => ({
  eventName: 'booking_charge_to_card_alert_change_payment_method_clicked',
  screen: ANALYTIKA_SCREENS.MARKETPLACE_REQUEST_DELIVERY
});

export const negativeWalletBalanceAlertDisplayed = () => ({
  eventName: 'negative_wallet_balance_alert_displayed',
  screen: ANALYTIKA_SCREENS.MARKETPLACE_REQUEST_DELIVERY
});

export const negativeWalletBalanceAlertTopUpClicked = () => ({
  eventName: 'negative_wallet_balance_alert_top_up_clicked',
  screen: ANALYTIKA_SCREENS.MARKETPLACE_REQUEST_DELIVERY
});

export const walletWidgetOnHover = (
  screen: ANALYTIKA_SCREENS,
  value?: { wallet_balance: number; default_payment_method_flag: boolean }
) => ({
  eventName: 'wallet_widget_on_hover',
  screen,
  value: JSON.stringify(value)
});

export const walletWidgetRechargeClicked = (
  screen: ANALYTIKA_SCREENS,
  value?: { wallet_balance: number; wallet_widget_tag: string }
) => ({
  eventName: 'wallet_widget_recharge_clicked',
  screen,
  value: JSON.stringify(value)
});

export const customerPhoneNumberValidationError = (value?: { phone_number: string }) => ({
  eventName: 'invalid_customer_phone_number_entered',
  screen: ANALYTIKA_SCREENS.MARKETPLACE_REQUEST_DELIVERY,
  value: JSON.stringify(value)
});

export const contactPhoneNumberValidationError = (value?: { phone_number: string }) => ({
  eventName: 'invalid_contact_phone_number_entered',
  screen: ANALYTIKA_SCREENS.MARKETPLACE_REQUEST_DELIVERY,
  value: JSON.stringify(value)
});

export const addNewAddressClicked = (
  screen: ANALYTIKA_SCREENS,
  value?: { pickup_location: string; dropoff_location: string }
) => ({
  eventName: 'add_new_address_clicked',
  screen,
  value: JSON.stringify(value)
});

export const addNewAddressScreenOpen = () => ({
  eventName: 'add_new_address_screen_open',
  screen: ANALYTIKA_SCREENS.ADD_NEW_ADDRESS
});

export const saveThisAddressClicked = (value: {
  city: string;
  street: string;
  lat: number;
  lng: number;
  building: string;
  additional_details: string;
  address_nickname: string;
  default_address_checked: boolean;
}) => ({
  eventName: 'save_this_address_clicked',
  value: JSON.stringify(value),
  screen: ANALYTIKA_SCREENS.ADD_NEW_ADDRESS
});

export const cancelAddNewAddressClicked = () => ({
  eventName: 'cancel_add_new_address_clicked',
  screen: ANALYTIKA_SCREENS.ADD_NEW_ADDRESS
});

export const dropofPinDragged = (screen: ANALYTIKA_SCREENS, value?: { dropoff_lat: number; dropoff_long: number }) => ({
  eventName: 'dropoff_pin_dragged',
  screen,
  value: JSON.stringify(value)
});

export const emptyCustomerPhoneNumberError = () => ({
  eventName: 'empty_customer_phone_number_error',
  screen: ANALYTIKA_SCREENS.MARKETPLACE_REQUEST_DELIVERY
});

export const emptyContactPhoneNumberError = () => ({
  eventName: 'empty_user_phone_number_error',
  screen: ANALYTIKA_SCREENS.MARKETPLACE_REQUEST_DELIVERY
});

export const emptyDropOffError = () => ({
  eventName: 'drop_off_empty_error',
  screen: ANALYTIKA_SCREENS.MARKETPLACE_REQUEST_DELIVERY
});

export const samePickupDropOffError = () => ({
  eventName: 'same_pickup_dropoff_error',
  screen: ANALYTIKA_SCREENS.MARKETPLACE_REQUEST_DELIVERY
});

export const pickupLocationChanged = (value?: { pick_up_location_id: string | number }) => ({
  eventName: 'pick_up_location_changed',
  screen: ANALYTIKA_SCREENS.MARKETPLACE_REQUEST_DELIVERY,
  value: JSON.stringify(value)
});

export const dropOffLocationChanged = (value?: { drop_off_location_id: string }) => ({
  eventName: 'drop_off_location_changed',
  screen: ANALYTIKA_SCREENS.MARKETPLACE_REQUEST_DELIVERY,
  value: JSON.stringify(value)
});

export const nearbyLocationNotFound = (value: LatLng) => ({
  eventName: 'no_nearby_location',
  screen: ANALYTIKA_SCREENS.MARKETPLACE_REQUEST_DELIVERY,
  value: JSON.stringify(value)
});

export const customDropoffSubmitClicked = (value: SearchLocation | null | undefined) => ({
  eventName: 'custom_dropoff_submit_clicked',
  screen: ANALYTIKA_SCREENS.MARKETPLACE_REQUEST_DELIVERY,
  value: JSON.stringify(value)
});

export const customDropoffCancelClicked = () => ({
  eventName: 'custom_dropoff_cancel_clicked',
  screen: ANALYTIKA_SCREENS.MARKETPLACE_REQUEST_DELIVERY
});

export const customDropoffConfirmClicked = (value: SearchLocation | undefined | null) => ({
  eventName: 'custom_dropoff_confirm_clicked',
  screen: ANALYTIKA_SCREENS.MARKETPLACE_REQUEST_DELIVERY,
  value: JSON.stringify(value)
});

export const customDropoffBackClicked = () => ({
  eventName: 'custom_dropoff_back_clicked',
  screen: ANALYTIKA_SCREENS.MARKETPLACE_REQUEST_DELIVERY
});

export const customDropoffSetLocationOnMapClicked = () => ({
  eventName: 'custom_dropoff_set_location_on_map_clicked',
  screen: ANALYTIKA_SCREENS.MARKETPLACE_REQUEST_DELIVERY
});

export const newDeliveryCTAMenuClicked = (screen: ANALYTIKA_SCREENS) => ({
  eventName: 'markteplace_menu_new_delivery_clicked',
  screen: screen
});

export const marketplaceActiveMenuClicked = (screen: ANALYTIKA_SCREENS) => ({
  eventName: 'marketplace_menu_active_deliveries_clicked',
  screen: screen
});

export const marketplaceCompletedMenuClicked = (screen: ANALYTIKA_SCREENS) => ({
  eventName: 'marketplace_menu_completed_deliveries_clicked',
  screen: screen
});

export const marketplaceDashboardMenuClicked = (screen: ANALYTIKA_SCREENS) => ({
  eventName: 'marketplace_menu_dashboard_clicked',
  screen: screen
});

export const dedicatedFleetGenerateOTPMenuClicked = (screen: ANALYTIKA_SCREENS) => ({
  eventName: 'dedicated_fleet_menu_generate_otp_clicked',
  screen: screen
});

export const dedicatedFleetActiveMenuClicked = (screen: ANALYTIKA_SCREENS) => ({
  eventName: 'dedicated_fleet_menu_active_deliveries',
  screen: screen
});

export const profileMenuClicked = (screen: ANALYTIKA_SCREENS) => ({
  eventName: 'profile_menu_clicked',
  screen: screen
});

export const faqMenuClicked = (screen: ANALYTIKA_SCREENS) => ({
  eventName: 'faq_menu_clicked',
  screen: screen
});

export const genericPaymentMessage = (message: any) => ({
  eventName: 'payments_message',
  value: JSON.stringify(message),
  screen: 'no_screen'
});

export const primaryPaymentMethodNotPresent = (methodId: number) => ({
  eventName: 'payments_primary_method_not_present',
  value: JSON.stringify(methodId),
  screen: 'no_screen'
});

export const secondaryPaymentMethodNotPresent = (methodId: number) => ({
  eventName: 'payments_secondary_method_not_present',
  value: JSON.stringify(methodId),
  screen: 'no_screen'
});

export const accountDisabledRedirect = (screen: ANALYTIKA_SCREENS) => ({
  eventName: 'account_disabled_redirect',
  screen: screen || 'no_screen'
});

export const noPortalAccessRedirect = (screen: ANALYTIKA_SCREENS) => ({
  eventName: 'no_portal_access_redirect',
  screen: screen || 'no_screen'
});

export const otpDisabledRedirect = (screen: ANALYTIKA_SCREENS) => ({
  eventName: 'otp_disabled_redirect',
  screen: screen || 'no_screen'
});
